import { Component, output, inject, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { UnidadNegocio } from 'src/app/domain/model/agenda/agenda';
import {
  DireccionUnidadNegocio,
  UnidadNegocioRepository
} from 'src/app/domain/service/unidad-negocio/unidad-negocio-repository';
import { busquedaUnidadNegocioPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { sanitizeText } from 'src/app/common/input-utils';

/**
 * Filtros para búsqueda de unidades de negocio.
 * Legacy: FilterUnidadNegocioViewImpl.java - tbCodigo, tbNombre
 */
export interface FiltroUnidadNegocio {
  codigo?: string;
  nombre?: string;
}

/**
 * Diálogo de búsqueda de Unidades de Negocio.
 * Replica exactamente el legacy GWT: FilterUnidadNegocioViewImpl.java
 *
 * Tabla: TPSC_UNIDADES_NEGOCIO
 * SQL: SELECT nmconsecutivo, dsunidad FROM Tpsc_unidades_negocio WHERE febaja IS NULL
 *
 * Filtros:
 * - Código (nmconsecutivo)
 * - Nombre (dsunidad)
 *
 * Formato display: "código - nombre" (ej: "3 - RIESGOS LABORALES ARL")
 *
 * Usado para tipo de cita CONTENIDOS_TECNICOS (54)
 *
 * @author Ingeneo - Migración GWT a Angular
 */
@Component({
  selector: 'app-busqueda-unidad-negocio-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Búsqueda de Unidad de Negocio"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Sección Filtros de Búsqueda -->
      <div
        class="border rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- Código -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Código</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.codigo"
              (input)="filtro.codigo = sanitizeText(filtro.codigo)"
              class="w-full"
              maxlength="10"
              placeholder="Ej: 3"
              (keyup.enter)="buscar()"
            />
          </div>

          <!-- Nombre -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Nombre</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.nombre"
              (input)="filtro.nombre = sanitizeText(filtro.nombre)"
              class="w-full"
              maxlength="100"
              placeholder="Ej: RIESGOS"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>

        <!-- Botones de filtro -->
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 mt-4 w-full"
        >
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-sm min-h-[44px]"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            icon="pi pi-eraser"
            label="Limpiar"
            class="p-button-sm p-button-secondary p-button-outlined min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </div>

      <!-- Mensaje de estado -->
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 text-yellow-800': mensajeTipo() === 'warning',
            'bg-blue-100 text-blue-800': mensajeTipo() === 'info',
            'bg-red-100 text-red-800': mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- Loading -->
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <!-- Tabla de resultados -->
      @if (!loading() && items().length > 0) {
        @if (totalRegistros() > 0) {
          <div
            class="flex justify-end p-3 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400"
          >
            Registros del {{ registroInicio() }} hasta el
            {{ registroFin() }} de {{ totalRegistros() }}
          </div>
        }
        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.nmconsecutivo) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(item.dsunidad)"
                >
                  {{ textoTabla(item.dsunidad) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.nmconsecutivo)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '250px'"
            [selectionMode]="tableSelectionMode()"
            [(selection)]="seleccionado"
            (onRowSelect)="onRowSelect($event)"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [dataKey]="
              (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
            "
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código">Código</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Nombre">Nombre</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item">
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.nmconsecutivo)"
                    >{{ textoTabla(item.nmconsecutivo) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dsunidad)"
                    >{{ textoTabla(item.dsunidad) }}</span
                  >
                </td>
                <td>
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionarItem(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Paginación por cursor del servidor (frontend-girop §17.7) -->
        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPaginas()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onSelectedPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="loadPreviousPage()"
          (next)="loadNextPage()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      @if (!loading() && busquedaRealizada && items().length === 0) {
        <div class="text-center text-gray-500 p-4 border rounded">
          No se encontraron unidades de negocio
        </div>
      }

      <!-- Botones footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaUnidadNegocioDialogComponent extends BaseBusquedaPaginadaDialog<UnidadNegocio> {
  // === Outputs ===
  onSelect = output<UnidadNegocio>();
  onClose = output<void>();

  // === Services ===
  private readonly unidadNegocioRepository = inject(UnidadNegocioRepository);

  // === State ===
  visible = true;
  filtro: FiltroUnidadNegocio = {};

  /** Sanitización XSS/SQLi (seguridad-girop §3) para filtros de texto libre */
  sanitizeText = sanitizeText;
  seleccionado: UnidadNegocio | null = null;

  readonly tableConfig = computed(() =>
    busquedaUnidadNegocioPreset.createConfig({
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-unidad-negocio',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron unidades de negocio'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de unidades de negocio',
        announceSelection: true,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  readonly tableSelectionMode = this.buildTableSelectionMode(this.tableConfig);

  // Flags de navegación: los fija la RESPUESTA del backend (frontend-girop §17.7).
  // Sustituyen a los computed de la base paginada (que derivan de paginaActual).
  override hasNext = signal(false);
  override hasPrevious = signal(false);

  /** Cursores opacos de la última respuesta: se reenvían tal cual. */
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;

  /**
   * Filtro de la búsqueda vigente. La navegación entre páginas lo reutiliza para
   * que el cursor corresponda a la misma consulta aunque el usuario edite los campos.
   */
  private filtroAplicado: FiltroUnidadNegocio = {};

  /** Cambio de tamaño de página (base): vuelve a la página 1 de la búsqueda vigente. */
  protected async ejecutarBusqueda(_start: number): Promise<void> {
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Inicializa el diálogo cargando todas las unidades de negocio.
   * Legacy: Al abrir el filtro se muestran todas las activas.
   */
  async ngOnInit(): Promise<void> {
    await this.cargarTodas();
  }

  /**
   * Primera página de las unidades de negocio activas.
   * Legacy: SELECT nmconsecutivo, dsunidad FROM Tpsc_unidades_negocio WHERE febaja IS NULL
   */
  async cargarTodas(): Promise<void> {
    this.filtroAplicado = {};
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Busca unidades de negocio con los filtros actuales (siempre desde la página 1).
   * Legacy: FilterUnidadNegocioViewImpl.siAccionFiltro()
   */
  async buscar(): Promise<void> {
    // Sanitización XSS de texto libre antes de enviar (seguridad-girop §3).
    this.filtro = {
      codigo: sanitizeText(this.filtro.codigo) || undefined,
      nombre: sanitizeText(this.filtro.nombre) || undefined
    };
    this.filtroAplicado = { ...this.filtro };
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Limpia los filtros y recarga todas las unidades.
   */
  async limpiarFiltros(): Promise<void> {
    this.filtro = {};
    this.clearMensajes();
    await this.cargarTodas();
  }

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) return;
    await this.cargarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return;
    await this.cargarPagina(
      this.previousCursorActual,
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) return;
    await this.cargarPagina(this.nextCursorActual, 'NEXT', this.paginaActual() + 1);
  }

  /** Última página: direction=LAST con cursor nulo; la página se calcula con la respuesta. */
  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) return;
    await this.cargarPagina(null, 'LAST', 1);
  }

  /**
   * Pide una página al backend con el filtro vigente. La página, los cursores y los
   * flags solo cambian con una respuesta exitosa; ante error se conserva la página.
   */
  private async cargarPagina(
    cursor: string | null,
    direction: DireccionUnidadNegocio,
    pagina: number
  ): Promise<void> {
    const codigo = this.filtroAplicado.codigo?.trim() || undefined;
    const nombre = this.filtroAplicado.nombre?.trim() || undefined;
    const conFiltros = !!(codigo || nombre);
    const size = this.pageSize();

    this.loading.set(true);
    this.mensaje.set('');
    this.busquedaRealizada = false;

    try {
      const response = conFiltros
        ? await this.unidadNegocioRepository.buscar(codigo, nombre, cursor, size, direction)
        : await this.unidadNegocioRepository.listarTodas(cursor, size, direction);

      const content = response?.content ?? [];
      const total = response?.totalElements ?? 0;
      this.items.set(content);
      this.totalRegistros.set(total);
      this.nextCursorActual = response?.nextCursor ?? null;
      this.previousCursorActual = response?.previousCursor ?? null;
      this.hasNext.set(response?.hasNext ?? false);
      this.hasPrevious.set(response?.hasPrevious ?? false);
      this.paginaActual.set(
        direction === 'LAST'
          ? size > 0 && total > 0
            ? Math.max(1, Math.ceil(total / size))
            : 1
          : pagina
      );
      this.busquedaRealizada = true;

      if (content.length === 0) {
        this.setMensaje(
          conFiltros ? 'No se encontraron resultados' : 'No hay unidades de negocio activas',
          conFiltros ? 'warning' : 'info'
        );
      }
    } catch (error) {
      console.error('Error buscando unidades de negocio:', error);
      this.setMensaje(
        this.resolverMensajeErrorBusqueda(
          error,
          conFiltros ? 'Error en la búsqueda' : 'Error al cargar unidades de negocio'
        ),
        'error'
      );
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Maneja la selección de una fila en la tabla.
   * El tipo de PrimeNG TableRowSelectEvent incluye data como posiblemente undefined o array.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowSelect(event: any): void {
    if (event.data && !Array.isArray(event.data)) {
      this.seleccionarItem(event.data as UnidadNegocio);
    }
  }

  /**
   * Emite la unidad seleccionada y cierra el diálogo.
   * Legacy: siAccionAceptar() - emite el item seleccionado
   */
  seleccionarItem(item: UnidadNegocio): void {
    this.onSelect.emit(item);
    this.visible = false;
  }

  /**
   * Cierra el diálogo sin selección.
   */
  onCancelar(): void {
    this.onClose.emit();
    this.visible = false;
  }

  /**
   * Establece un mensaje de estado.
   */
  setMensaje(texto: string, tipo: 'info' | 'warning' | 'error'): void {
    this.mensaje.set(texto);
    this.mensajeTipo.set(tipo);
  }

}
